import { useState } from "react";

import { Classes, Spinner } from "@blueprintjs/core";
import some from "lodash/some";
import { Clock, Info } from "lucide-react";
import { Link, useNavigate } from "react-router";

import { pluralize } from "@/components/helpers/string-utils";
import { isWorkspaceAdmin } from "@/components/helpers/workspace/permissions";
import {
  TOC,
  TOCBox,
} from "@/components/pages_logged_in/workspace-admin/WorkspaceMemberTable";
import { DateTimeDisplay } from "@/components/pieces/DateTimeDisplay";
import { PlaybookEmptyProgramSvg } from "@/components/pieces/icons/svg/playbook-empty-program";
import { Header } from "@/components/pieces/table/Header";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { ProgramStatus } from "@/graphql";
import type { MiniProgram, ProgramTableField } from "@/types/programs";
import type { HeaderConfig } from "@/types/tables";

import { ProgramMenu } from "./ProgramMenu";
import { ProgramStatusBadge } from "./ProgramStatus";

interface ProgramHeaderConfig extends HeaderConfig {
  field: ProgramTableField;
}

const PARTICIPANT_HEADERS: ProgramHeaderConfig[] = [
  {
    field: "name",
    title: "Program",
    widthClass: "w-72",
  },
  {
    field: "status",
    title: "Status",
    widthClass: "w-32",
  },
  {
    field: "total_participant_ongoing_surveys",
    title: "Ongoing",
    disableSorting: true,
    widthClass: "w-24",
  },
  {
    field: "target_launch_date",
    title: "Timeline",
    justifyContent: "flex-end",
    widthClass: "w-80",
  },
];

const ADMIN_HEADERS: ProgramHeaderConfig[] = [
  {
    field: "name",
    title: "Program",
    widthClass: "w-72",
  },
  {
    field: "status",
    title: "Status",
    widthClass: "w-32",
  },
  {
    field: "participant_count",
    title: "Participants",
    justifyContent: "center",
    widthClass: "w-28",
  },
  {
    field: "target_launch_date",
    title: "Timeline",
    widthClass: "w-72",
  },
  {
    field: "actions",
    title: "Actions",
    disableSorting: true,
    justifyContent: "center",
    widthClass: "w-16",
  },
];

const MERGED_HEADERS: ProgramHeaderConfig[] = [
  {
    field: "name",
    title: "Program",
    widthClass: "w-60",
  },
  {
    field: "status",
    title: "Status",
    widthClass: "w-28",
  },
  {
    field: "participant_count",
    title: "Participants",
    justifyContent: "center",
    widthClass: "w-24",
  },
  {
    field: "total_participant_ongoing_surveys",
    title: "Ongoing",
    widthClass: "w-20",
  },
  {
    field: "target_launch_date",
    title: "Timeline",
    widthClass: "w-72",
  },
  {
    field: "actions",
    title: "Actions",
    disableSorting: true,
    justifyContent: "center",
    widthClass: "w-16",
  },
];

interface ProgramTimeLineProps {
  targetLaunchDate: string;
  status: ProgramStatus;
  targetEndDate?: string | null;
  closedAt?: string | null;
}

function ProgramTimeLine({
  targetLaunchDate,
  status,
  targetEndDate,
  closedAt,
}: ProgramTimeLineProps) {
  const formatString = "MMM d 'at' h:mm a";

  if (status === ProgramStatus.InDesign) {
    return (
      <div className="flex items-center text-gray-600">
        <DateTimeDisplay
          time={targetLaunchDate}
          formatString={formatString}
          showTooltip={true}
        />
        <SimpleTooltip content="Scheduled Launch">
          <Info className="ml-2 h-4 w-4 text-gray-500" />
        </SimpleTooltip>
      </div>
    );
  }

  if (status === ProgramStatus.LaunchReady) {
    return (
      <div className="flex items-center text-gray-600">
        <DateTimeDisplay
          time={targetLaunchDate}
          formatString={formatString}
          showTooltip={true}
        />
        <SimpleTooltip content="Planned Launch">
          <Clock className="ml-2 h-4 w-4 text-gray-600" />
        </SimpleTooltip>
      </div>
    );
  }

  const endDate = targetEndDate || closedAt;

  if (endDate) {
    return (
      <div className="text-gray-600">
        <DateTimeDisplay
          time={targetLaunchDate}
          formatString={formatString}
          showTooltip={true}
        />
        {" - "}
        <DateTimeDisplay
          time={endDate}
          formatString={formatString}
          showTooltip={true}
        />
      </div>
    );
  }
  return (
    <DateTimeDisplay
      time={targetLaunchDate}
      formatString={formatString}
      showTooltip={true}
    />
  );
}

interface ProgramTableProps {
  loading?: boolean;
  programs: MiniProgram[];
  currentSort: { field?: ProgramTableField; asc?: boolean };
  onSort: (field: ProgramTableField) => void;
  onUpdateProgram: (program: MiniProgram) => void;
  onCloneProgram: (program: MiniProgram) => void;
  onArchiveProgram: (program: MiniProgram) => void;
  onEndProgram: (program: MiniProgram) => void;
}

export function ProgramTable({
  loading = false,
  programs,
  currentSort,
  onSort,
  onUpdateProgram,
  onCloneProgram,
  onArchiveProgram,
  onEndProgram,
}: ProgramTableProps) {
  const isAdmin = isWorkspaceAdmin();
  const navigate = useNavigate();

  // is program lead of at least one program
  const hasProgramLeadRole = some(programs || [], { isProgramLead: true });

  // is participant of at least one program
  const hasParticipantRole = some(programs || [], {
    isProgramParticipant: true,
  });

  const isDeletedUser = !hasProgramLeadRole && !hasParticipantRole && !isAdmin;

  const [selectedProgramId, setSelectedProgramId] = useState<string>();

  let headers = PARTICIPANT_HEADERS;

  const isAdminView = isAdmin || hasProgramLeadRole;

  if (isAdminView && !hasParticipantRole) {
    headers = ADMIN_HEADERS;
  }

  if (isAdminView && hasParticipantRole) {
    headers = MERGED_HEADERS;
  }

  const handleSelectProgram = (event: React.MouseEvent, programId: string) => {
    const to = `/programs/${programId}`;
    if (event.metaKey || event.ctrlKey) {
      // Open in a new tab if Ctrl or Cmd key is pressed
      window.open(window.location.origin + to, "_blank");
    } else {
      event.preventDefault();
      navigate(to);
    }
  };

  return (
    <TOCBox width="100%" height="100%">
      <TOC className={`${Classes.HTML_TABLE} noboder-table table-fixed`}>
        <thead>
          <tr>
            {headers.map((config) => (
              <Header
                key={config.field}
                config={config}
                currentSort={currentSort}
                onSort={onSort}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {programs.map((row) => {
            const {
              id,
              name,
              status,
              participantCount,
              totalParticipantOngoingSurveys,
              isProgramLead,
              targetLaunchDate,
              targetEndDate,
              closedAt,
            } = row || {};
            const isOpen = id === selectedProgramId;
            const isShowMenu = isAdmin || isProgramLead;
            return (
              <tr
                key={`program-item-${id}-huy`}
                onClick={(e) => handleSelectProgram(e, id)}
                data-test-id="program-row"
              >
                <td
                  data-test-id="cell-program-name"
                  className={
                    headers.find((h) => h.field === "name")?.widthClass || ""
                  }
                >
                  <Link
                    onClick={(e) => e.preventDefault()}
                    to={`/programs/${id}`}
                    className="block text-base font-semibold text-gray-900 truncate hover:text-blue-600 transition-colors"
                    title={name} // Add title attribute for full name on hover
                  >
                    {name}
                  </Link>
                </td>
                <td
                  data-test-id="cell-program-status"
                  className={
                    headers.find((h) => h.field === "status")?.widthClass || ""
                  }
                >
                  <ProgramStatusBadge status={status} />
                </td>
                {isAdminView && (
                  <td
                    data-test-id="cell-program-participants"
                    className={`text-center ${headers.find((h) => h.field === "participant_count")?.widthClass || ""}`}
                  >
                    {participantCount ? participantCount : "-"}
                  </td>
                )}
                {(hasParticipantRole || isDeletedUser) && (
                  <td
                    className={
                      headers.find(
                        (h) => h.field === "total_participant_ongoing_surveys",
                      )?.widthClass || ""
                    }
                  >
                    {totalParticipantOngoingSurveys
                      ? `${pluralize({
                          amount: totalParticipantOngoingSurveys,
                          singular: "component",
                          plural: "components",
                        })}`
                      : null}
                  </td>
                )}
                <td
                  className={`${isAdminView ? "text-left" : "text-right"} ${headers.find((h) => h.field === "target_launch_date")?.widthClass || ""}`}
                  data-test-id="cell-program-timeline"
                >
                  {targetLaunchDate && (
                    <div className="flex items-center">
                      <ProgramTimeLine
                        targetLaunchDate={targetLaunchDate}
                        targetEndDate={targetEndDate}
                        closedAt={closedAt}
                        status={status || ProgramStatus.InDesign}
                      />
                    </div>
                  )}
                </td>
                {isShowMenu && (
                  <td
                    className={`text-center ${headers.find((h) => h.field === "actions")?.widthClass || ""}`}
                    data-test-id="cell-program-actions"
                  >
                    <ProgramMenu
                      program={row}
                      status={status || ProgramStatus.InDesign}
                      isOpen={isOpen}
                      onUpdateProgram={() => {
                        setSelectedProgramId(undefined);
                        onUpdateProgram(row);
                      }}
                      onCloneProgram={() => {
                        setSelectedProgramId(undefined);
                        onCloneProgram(row);
                      }}
                      onArchiveProgram={() => {
                        setSelectedProgramId(undefined);
                        onArchiveProgram(row);
                      }}
                      onEndProgram={() => {
                        setSelectedProgramId(undefined);
                        onEndProgram(row);
                      }}
                      onClose={() => setSelectedProgramId(undefined)}
                      onClick={() => {
                        setSelectedProgramId(id);
                      }}
                    />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </TOC>

      {!programs.length ? (
        <div className="bg-white min-h-60 w-full py-6 flex justify-center items-center flex-col">
          {loading ? (
            ""
          ) : (
            <>
              <PlaybookEmptyProgramSvg size={62} />
              <div className="mt-3 text-gray-600 text-center">
                No programs to show.
              </div>
            </>
          )}
        </div>
      ) : null}

      {loading && (
        <div className="flex justify-center p-2 absolute top-0 left-0 right-0 opacity-50 w-full h-full bg-white">
          <Spinner intent="primary" />
        </div>
      )}
    </TOCBox>
  );
}
